import { useCallback, useEffect, useReducer } from 'react';
import { Link, useParams } from 'react-router-dom';

import { useFetch } from 'api';
import { useTranslationX } from 'i18n';
import { useAppContext } from 'context';
import { Event } from 'types';
import { AngleLeft } from 'assets/vector';
import { SignInButton } from 'components';
import { Button, Loader } from '../ui';
import { useEventStateHandler } from 'hooks';
import { classes } from 'utils';
import Feed from './Feed';
import ScoreBoard from './ScoreBoard';

import styles from './PlayHome.module.scss';

type Props = {
  userId: string;
  events: Event[];
  state: State;
  dispatch: React.Dispatch<Action>;
};

type State = { loading: boolean; error?: string };

type Action = { type: 'LOADED' } | { type: 'ERROR'; payload: string };

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'LOADED':
      return { ...state, loading: false };

    case 'ERROR':
      return { ...state, error: action.payload };

    default:
      return state;
  }
};

const PlayHome = () => {
  const [state, dispatch] = useReducer(reducer, { loading: true });

  const { auth, setOverlay } = useAppContext();
  const { pathname } = useParams<{ pathname: string }>();
  const { tx } = useTranslationX('play.home');

  const { data, error } = useFetch<Event[]>(
    `/v1/webapp/game/events?pathname=${pathname}`
  );

  useEffect(() => {
    if (!auth.validation && !state.loading) {
      setOverlay({ type: 'signin', delay: 3000 });
    }
  }, [auth.validation, setOverlay, state.loading]);

  useEffect(() => () => setOverlay(null), [setOverlay]);

  const errorMessage = error ? tx('error.notFound') : state.error;

  const renderContent = () => {
    if (errorMessage) {
      return <div className={styles.error}>{errorMessage}</div>;
    }

    if (data) {
      if (auth.userId) {
        return (
          <PlayHomeContent
            userId={auth.userId}
            events={data}
            state={state}
            dispatch={dispatch}
          />
        );
      }

      return <div className={styles.error}>{tx('error.unknown')}</div>;
    }

    return null;
  };

  return (
    <div className={styles.playHome}>
      <div className={styles.header}>
        <Link to="/">
          <div>
            <AngleLeft className={styles.icon} />
            <span className={styles.text}>{tx('btBack')}</span>
          </div>
        </Link>

        {!state.loading ? (
          <div className={styles.controls}>
            <SignInButton />
          </div>
        ) : null}
      </div>

      <div className={styles.body}>
        {renderContent()}
        <Loader visible={state.loading && !errorMessage} />
      </div>
    </div>
  );
};

const PlayHomeContent = (props: Props) => {
  const { state, dispatch, ...rest } = props;
  const eventstate = useEventStateHandler(rest);
  const { overlay, setOverlay } = useAppContext();

  const { tx, t } = useTranslationX('play.home');
  const { status, game, messages, change, loadMessages } = eventstate;

  useEffect(() => {
    if (status === 'error') {
      dispatch({ type: 'ERROR', payload: tx('error.unknown') });
    }
  }, [dispatch, status, tx]);

  const onLoad = useCallback(() => dispatch({ type: 'LOADED' }), [dispatch]);

  return status === 'active' ? (
    <div className={classes(styles.content, state.loading ? '' : styles.active)}>
      {game ? <ScoreBoard data={game} /> : null}

      <Button
        onClick={() =>
          game
            ? setOverlay(
                overlay?.type === 'plays'
                  ? null
                  : { type: 'plays', payload: { events: rest.events, game } }
              )
            : null
        }
        style={{ root: { marginBottom: '0.5rem' } }}
      >
        {t('play.plays.button')}
      </Button>

      <Feed
        messages={messages}
        change={change}
        loadMessages={loadMessages}
        onLoad={onLoad}
      />
    </div>
  ) : null;
};

export default PlayHome;
